// src/pages/ConfigPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext'; // Seu contexto de usuário
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateProfile,
  updatePassword,
  updateEmail, // Importado para alteração de e-mail
  signOut,
} from 'firebase/auth';
import { auth } from '../services/firebase'; // Configuração do Firebase Auth
import { doc, updateDoc, onSnapshot, getDoc } from 'firebase/firestore'; // Importado getDoc e onSnapshot
import { db } from '../services/firebase'; // Configuração do Firebase Firestore

// Componente de Modal Simples para substituir alert()
const CustomModal = ({ message, type, onClose }) => {
  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
  const textColor = 'text-white';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
        <div
          className={`p-3 rounded-full inline-flex items-center justify-center ${bgColor} mb-4`}
        >
          {type === 'success' ? (
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
          ) : (
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          )}
        </div>
        <p
          className={`text-lg font-semibold ${textColor === 'text-white' ? 'text-gray-800' : textColor} mb-4`}
        >
          {message}
        </p>
        <button
          onClick={onClose}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
        >
          OK
        </button>
      </div>
    </div>
  );
};

export default function ConfigPage() {
  const { usuario } = useAuth(); // `usuario` é o objeto User do Firebase Auth

  // Estados dos dados pessoais (do Auth e Firestore)
  const [nome, setNome] = useState(usuario?.displayName || '');
  const [telefone, setTelefone] = useState(''); // Será carregado do Firestore
  const [dataNascimento, setDataNascimento] = useState(''); // Será carregado do Firestore
  const [cpf, setCpf] = useState(''); // Será carregado do Firestore (somente leitura)
  const [editingPersonalData, setEditingPersonalData] = useState(false); // Alterna modo de edição

  // Estados para alteração de senha
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState('');

  // Estados para alteração de e-mail
  const [novoEmail, setNovoEmail] = useState('');
  const [senhaAtualEmail, setSenhaAtualEmail] = useState('');

  // Estados para informações de assinatura
  const [statusAssinatura, setStatusAssinatura] = useState('Carregando...');
  const [tipoAssinatura, setTipoAssinatura] = useState('N/A');
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState(null);

  // Estados para o modal de mensagem
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('success'); // 'success' ou 'error'

  // Estado para o modal de confirmação de cancelamento
  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);

  // Função para exibir o modal
  const showCustomModal = (message, type) => {
    setModalMessage(message);
    setModalType(type);
    setShowModal(true);
  };

  // Função para fechar o modal
  const closeCustomModal = () => {
    setShowModal(false);
    setModalMessage('');
  };

  // Efeito para carregar dados do Firestore e monitorar mudanças
  useEffect(() => {
    if (usuario) {
      const userDocRef = doc(db, 'usuarios', usuario.uid);
      const unsubscribe = onSnapshot(
        userDocRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setNome(data.nomeCompleto || usuario.displayName || '');
            setTelefone(data.telefone || '');
            setDataNascimento(data.dataNascimento || ''); // Assume que dataNascimento é uma string 'YYYY-MM-DD'
            setCpf(data.cpf || ''); // CPF é somente leitura
            setStatusAssinatura(
              data.statusAssinatura ||
                (data.assinaturaAtiva ? 'ativa' : 'inativa') ||
                'inativa'
            );
            setTipoAssinatura(data.tipoAssinatura || 'N/A');
            setStripeCustomerId(data.stripeCustomerId || null);
            setStripeSubscriptionId(data.stripeSubscriptionId || null); // Certifique-se de que este campo está sendo salvo no webhook
          } else {
            console.log('Documento do usuário não encontrado no Firestore.');
            setStatusAssinatura('inativa');
          }
        },
        (error) => {
          console.error(
            'Erro ao carregar dados do usuário do Firestore:',
            error
          );
          showCustomModal('Erro ao carregar dados do usuário.', 'error');
        }
      );

      // Limpa o listener quando o componente é desmontado
      return () => unsubscribe();
    }
  }, [usuario]); // Depende do objeto 'usuario' do Auth

  // Salvar dados pessoais editados
  const handleSalvarDadosPessoais = async () => {
    try {
      if (usuario) {
        // Atualiza displayName no Firebase Auth
        await updateProfile(usuario, { displayName: nome });

        // Atualiza dados no Firestore
        const docRef = doc(db, 'usuarios', usuario.uid);
        await updateDoc(docRef, {
          nomeCompleto: nome, // Use nomeCompleto para consistência com o backend
          telefone: telefone,
          dataNascimento: dataNascimento,
        });
        setEditingPersonalData(false);
        showCustomModal('Dados pessoais atualizados com sucesso!', 'success');
      }
    } catch (error) {
      console.error('Erro ao atualizar dados pessoais:', error);
      showCustomModal('Erro ao salvar dados pessoais.', 'error');
    }
  };

  // Trocar senha com reautenticação
  const handleTrocarSenha = async () => {
    if (novaSenha !== confirmarNovaSenha) {
      showCustomModal('As novas senhas não coincidem.', 'error');
      return;
    }
    if (novaSenha.length < 6) {
      // Firebase Auth exige no mínimo 6 caracteres
      showCustomModal('A nova senha deve ter no mínimo 6 caracteres.', 'error');
      return;
    }
    if (!senhaAtual) {
      showCustomModal('Por favor, informe a senha atual.', 'error');
      return;
    }

    try {
      // Passo 1: Criar credential com email e senha atual
      const credential = EmailAuthProvider.credential(
        usuario.email,
        senhaAtual
      );

      // Passo 2: Reautenticar o usuário
      await reauthenticateWithCredential(usuario, credential);

      // Passo 3: Atualizar senha
      await updatePassword(usuario, novaSenha);

      // Limpar campos
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarNovaSenha('');

      showCustomModal('Senha alterada com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao trocar senha:', error);
      if (error.code === 'auth/wrong-password') {
        showCustomModal('Senha atual incorreta.', 'error');
      } else if (error.code === 'auth/weak-password') {
        showCustomModal(
          'A nova senha é muito fraca. Escolha uma senha mais forte.',
          'error'
        );
      } else if (error.code === 'auth/requires-recent-login') {
        showCustomModal(
          'Por segurança, faça login novamente e tente outra vez.',
          'error'
        );
      } else {
        showCustomModal('Erro ao trocar senha.', 'error');
      }
    }
  };

  // Trocar e-mail com reautenticação
  const handleTrocarEmail = async () => {
    if (!novoEmail || !senhaAtualEmail) {
      showCustomModal(
        'Por favor, preencha todos os campos para alterar o e-mail.',
        'error'
      );
      return;
    }
    if (novoEmail === usuario.email) {
      showCustomModal('O novo e-mail é o mesmo que o e-mail atual.', 'error');
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(
        usuario.email,
        senhaAtualEmail
      );

      await reauthenticateWithCredential(usuario, credential);
      await updateEmail(usuario, novoEmail);

      // O e-mail no Firebase Auth foi atualizado.
      // O Firestore pode ser atualizado automaticamente se você tiver um Cloud Function que ouve Auth changes,
      // ou você pode atualizar manualmente aqui se necessário (mas o email do Auth já é a fonte primária).
      // Se você quiser atualizar o campo 'email' no Firestore também:
      // const docRef = doc(db, 'usuarios', usuario.uid);
      // await updateDoc(docRef, { email: novoEmail });

      setNovoEmail('');
      setSenhaAtualEmail('');
      showCustomModal(
        'E-mail alterado com sucesso! Você pode precisar fazer login novamente.',
        'success'
      );
      // Opcional: Forçar logout para que o usuário faça login com o novo e-mail
      // await signOut(auth);
      // window.location.href = '/login';
    } catch (error) {
      console.error('Erro ao trocar e-mail:', error);
      if (error.code === 'auth/wrong-password') {
        showCustomModal(
          'Senha atual incorreta para alterar o e-mail.',
          'error'
        );
      } else if (error.code === 'auth/email-already-in-use') {
        showCustomModal('Este e-mail já está em uso por outra conta.', 'error');
      } else if (error.code === 'auth/requires-recent-login') {
        showCustomModal(
          'Por segurança, faça login novamente e tente outra vez.',
          'error'
        );
      } else {
        showCustomModal('Erro ao trocar e-mail.', 'error');
      }
    }
  };

  // Gerenciar Assinatura (Redireciona para o Portal do Cliente Stripe)
  const handleGerenciarAssinatura = async () => {
    if (!stripeCustomerId) {
      showCustomModal(
        'ID de cliente Stripe não encontrado. Por favor, entre em contato com o suporte.',
        'error'
      );
      return;
    }

    try {
      // Chama a função Firebase para criar a sessão do Portal do Cliente
      // Certifique-se de que o URL da sua função é acessível
      const response = await fetch(
        'https://us-central1-elioncadastro.cloudfunctions.net/createStripeCustomerPortalSession',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Se você usa tokens de autenticação no frontend para suas funções, adicione aqui
            // 'Authorization': `Bearer ${await usuario.getIdToken()}`,
          },
          body: JSON.stringify({ customerId: stripeCustomerId }),
        }
      );

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url; // Redireciona para o Portal do Cliente
      } else {
        console.error('Erro ao obter URL do Portal do Cliente:', data.error);
        showCustomModal(
          'Erro ao gerenciar assinatura. Tente novamente mais tarde.',
          'error'
        );
      }
    } catch (error) {
      console.error('Erro de rede ao gerenciar assinatura:', error);
      showCustomModal(
        'Erro de conexão ao tentar gerenciar assinatura.',
        'error'
      );
    }
  };

  // Cancelar Assinatura (com modal de confirmação)
  const handleConfirmCancel = async () => {
    setShowConfirmCancelModal(false); // Fecha o modal de confirmação
    if (!stripeSubscriptionId) {
      showCustomModal(
        'ID da assinatura Stripe não encontrado. Não é possível cancelar.',
        'error'
      );
      return;
    }

    try {
      const response = await fetch(
        'https://us-central1-elioncadastro.cloudfunctions.net/cancelStripeSubscription',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Se você usa tokens de autenticação no frontend para suas funções, adicione aqui
            // 'Authorization': `Bearer ${await usuario.getIdToken()}`,
          },
          body: JSON.stringify({
            subscriptionId: stripeSubscriptionId,
            userId: usuario.uid,
          }), // Passa userId para atualização no Firestore
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        showCustomModal(
          'Assinatura cancelada com sucesso. Você ainda terá acesso até o final do período pago.',
          'success'
        );
        // O Firestore será atualizado pelo webhook de 'customer.subscription.deleted' do Stripe
        // ou pela sua função `cancelStripeSubscription` se ela fizer a atualização diretamente.
      } else {
        console.error('Erro ao cancelar assinatura:', data.error);
        showCustomModal(
          `Erro ao cancelar assinatura: ${data.error || 'Erro desconhecido'}`,
          'error'
        );
      }
    } catch (error) {
      console.error('Erro de rede ao cancelar assinatura:', error);
      showCustomModal(
        'Erro de conexão ao tentar cancelar assinatura.',
        'error'
      );
    }
  };

  // Encerrar todas as sessões (apenas desloga da sessão atual no cliente)
  const handleEncerrarSessoes = async () => {
    try {
      await signOut(auth);
      showCustomModal('Sessões encerradas. Você foi desconectado.', 'success');
      // Redireciona após um pequeno delay para o modal ser visto
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } catch (error) {
      console.error('Erro ao encerrar sessões:', error);
      showCustomModal('Erro ao encerrar sessões.', 'error');
    }
  };

  if (!usuario) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4 text-center">
        <p>Carregando informações do usuário ou usuário não autenticado...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">Configurações da Conta</h1>

      {/* Dados da Conta */}
      <div className="bg-white shadow p-4 rounded mb-6">
        <h2 className="text-xl font-semibold mb-4">👤 Dados da Conta</h2>
        {!editingPersonalData ? (
          <>
            <p>
              <strong>Nome:</strong> {nome}
            </p>
            <p>
              <strong>Email:</strong> {usuario.email}
            </p>
            <p>
              <strong>Telefone:</strong> {telefone || 'Não informado'}
            </p>
            <p>
              <strong>CPF:</strong> {cpf || 'Não informado'}
            </p>
            <p>
              <strong>Data de Nascimento:</strong>{' '}
              {dataNascimento || 'Não informado'}
            </p>
            <p>
              <strong>Data de cadastro:</strong>{' '}
              {usuario.metadata?.creationTime
                ? new Date(usuario.metadata.creationTime).toLocaleDateString()
                : 'N/A'}
            </p>
            <button
              onClick={() => setEditingPersonalData(true)}
              className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition duration-200"
            >
              Editar dados
            </button>
          </>
        ) : (
          <div className="space-y-3">
            <div>
              <label
                htmlFor="nome"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nome Completo
              </label>
              <input
                id="nome"
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome completo"
                className="w-full border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="telefone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Telefone
              </label>
              <input
                id="telefone"
                type="text"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                placeholder="Telefone"
                className="w-full border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="dataNascimento"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Data de Nascimento
              </label>
              <input
                id="dataNascimento"
                type="date" // Tipo date para seletor de data
                value={dataNascimento}
                onChange={(e) => setDataNascimento(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="cpf"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                CPF (somente leitura)
              </label>
              <input
                id="cpf"
                type="text"
                value={cpf || 'Não informado'}
                readOnly // CPF é somente leitura
                className="w-full border border-gray-300 px-3 py-2 rounded-md bg-gray-100 cursor-not-allowed"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSalvarDadosPessoais}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition duration-200"
              >
                Salvar
              </button>
              <button
                onClick={() => setEditingPersonalData(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md transition duration-200"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Alterar E-mail */}
      <div className="bg-white shadow p-4 rounded mb-6">
        <h2 className="text-xl font-semibold mb-4">📧 Alterar E-mail</h2>
        <div className="space-y-3">
          <div>
            <label
              htmlFor="novoEmail"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Novo E-mail
            </label>
            <input
              id="novoEmail"
              type="email"
              value={novoEmail}
              onChange={(e) => setNovoEmail(e.target.value)}
              placeholder="novo.email@example.com"
              className="w-full border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500"
              autoComplete="email"
            />
          </div>
          <div>
            <label
              htmlFor="senhaAtualEmail"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Sua Senha Atual
            </label>
            <input
              id="senhaAtualEmail"
              type="password"
              value={senhaAtualEmail}
              onChange={(e) => setSenhaAtualEmail(e.target.value)}
              placeholder="Sua senha atual"
              className="w-full border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500"
              autoComplete="current-password"
            />
          </div>
          <button
            onClick={handleTrocarEmail}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition duration-200"
          >
            Alterar E-mail
          </button>
        </div>
      </div>

      {/* Segurança (Alterar Senha) */}
      <div className="bg-white shadow p-4 rounded mb-6">
        <h2 className="text-xl font-semibold mb-4">
          🔒 Segurança (Alterar Senha)
        </h2>
        <div className="space-y-3">
          <div>
            <label
              htmlFor="senhaAtual"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Senha Atual
            </label>
            <input
              id="senhaAtual"
              type="password"
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
              placeholder="Senha atual"
              className="w-full border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label
              htmlFor="novaSenha"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nova Senha
            </label>
            <input
              id="novaSenha"
              type="password"
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              placeholder="Nova senha"
              className="w-full border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label
              htmlFor="confirmarNovaSenha"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirmar Nova Senha
            </label>
            <input
              id="confirmarNovaSenha"
              type="password"
              value={confirmarNovaSenha}
              onChange={(e) => setConfirmarNovaSenha(e.target.value)}
              placeholder="Confirmar nova senha"
              className="w-full border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-500"
              autoComplete="new-password"
            />
          </div>
          <button
            onClick={handleTrocarSenha}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition duration-200"
          >
            Alterar Senha
          </button>
        </div>
      </div>

      {/* Planos e Assinaturas */}
      <div className="bg-white shadow p-4 rounded mb-6">
        <h2 className="text-xl font-semibold mb-4">💳 Planos e Assinaturas</h2>
        <p className="mb-2">
          <strong>Status da Assinatura:</strong>{' '}
          <span
            className={`font-bold ${statusAssinatura === 'ativa' ? 'text-green-600' : 'text-red-600'}`}
          >
            {statusAssinatura.charAt(0).toUpperCase() +
              statusAssinatura.slice(1)}
          </span>
        </p>
        <p className="mb-4">
          <strong>Tipo de Plano:</strong> {tipoAssinatura}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleGerenciarAssinatura}
            className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2 rounded-md transition duration-200 flex-grow"
            disabled={!stripeCustomerId} // Desabilita se não houver customerId
          >
            Gerenciar Assinatura (Cartão, Faturas)
          </button>
          <button
            onClick={() => setShowConfirmCancelModal(true)}
            className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-md transition duration-200 flex-grow"
            disabled={statusAssinatura !== 'ativa' || !stripeSubscriptionId} // Só pode cancelar se estiver ativa e tiver subId
          >
            Cancelar Assinatura
          </button>
        </div>
      </div>

      {/* Encerrar Sessões (Apenas a atual) */}
      <div className="bg-white shadow p-4 rounded mb-6">
        <h2 className="text-xl font-semibold mb-4">🚪 Sair da Conta</h2>
        <p className="mb-4 text-gray-700">
          Clique no botão abaixo para desconectar-se desta sessão.
        </p>
        <button
          onClick={handleEncerrarSessoes}
          className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-md transition duration-200"
        >
          Sair
        </button>
      </div>

      {/* Modal de Mensagem Personalizado */}
      {showModal && (
        <CustomModal
          message={modalMessage}
          type={modalType}
          onClose={closeCustomModal}
        />
      )}

      {/* Modal de Confirmação de Cancelamento */}
      {showConfirmCancelModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
            <h3 className="text-xl font-semibold mb-4">
              Confirmar Cancelamento
            </h3>
            <p className="mb-6 text-gray-700">
              Tem certeza que deseja cancelar sua assinatura? Você perderá o
              acesso aos recursos premium ao final do seu ciclo de faturamento
              atual.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={handleConfirmCancel}
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
              >
                Sim, Cancelar
              </button>
              <button
                onClick={() => setShowConfirmCancelModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                Não, Manter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
